import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';

import { Box, Grid, MenuItem, Select, Typography } from '@mui/material';

import CustomButton from 'components/Button';
import { FilterPanelContainer, PageContainer } from 'components/GlobalStyles/styles';
import GridItem from 'components/GridItem';
import SideBarTabs from 'components/SideBarTabs';
import SnackBar from 'components/SnackBar';
import { CLIENT_MODAL, PROJECT_MODAL, projectModalActions } from 'features/Project/projectModalSlice';
import ProjectLineItem from 'pages/ProjectManagement/component/ProjectLineItem';
import { useGetManagerListQuery, useGetProjectClientQuery, useGetProjectsQuery } from 'services/busmanApi';
import { ClientProjectResponse, ProjectQueryParams, ProjectResponse } from 'types/ProjectTypes';
import { JobManagerType } from 'types/WorkLogTypes';
import { PROJECT_STATUS } from 'utils/constants';

import ClientContactModal from './component/ClientContactModal';
import ClientLineItem from './component/ClientLineItem';
import ProjectModal from './component/ProjectModal';
import { ProjectListContainer } from './styles';

const PROJECT_TAB = 0;
const CLIENT_TAB = 1;

/**
 * @url /project
 * @returns Project Management Page
 */
const ProjectManagement = () => {
  const dispatch = useDispatch();
  const { open, mode, modalProps } = useSelector((state: RootState) => state.projectModalReducer);

  const { data: jobManagers } = useGetManagerListQuery({});
  const { data: projectClients } = useGetProjectClientQuery({});
  const [sideTabIndex, setSideTabIndex] = useState(PROJECT_TAB);

  const [message, setMessage] = React.useState({ type: '', msg: '' });

  const [manager, setManager] = useState('all');
  const [client, setClient] = useState('all');
  const [jobStatus, setJobStatus] = useState(PROJECT_STATUS.all.value);

  const projectQueryParams = useMemo(() => {
    const kwargs = {} as ProjectQueryParams;
    if (jobStatus !== PROJECT_STATUS.all.value) {
      kwargs.projectStatus = jobStatus;
    }

    if (manager !== 'all') {
      kwargs.managerId = manager;
    }

    if (client !== 'all') {
      kwargs.clientContactId = client;
    }
    return kwargs;
  }, [jobStatus, client, manager]);
  const { data: projectList } = useGetProjectsQuery(projectQueryParams);

  const clientsWithAll = [
    { id: 'all', full_name: 'All' } as unknown as ClientProjectResponse,
    ...(projectClients ?? []).filter((c) => c.full_name !== null),
  ];

  const managerWithAll = [
    {
      user_id: 'all',
      email: 'all',
      user_role: 'A',
      full_name: 'All',
      is_active: true,
      is_staff: true,
    } as unknown as JobManagerType,
    ...(jobManagers || []).filter((item) => item.is_active === true),
  ];

  const formOptions = {
    managers: (jobManagers || []).filter((item) => item.is_active === true),
    clients: projectClients || [],
  };

  const sidePanel = () => {
    return (
      <FilterPanelContainer>
        <Typography variant="h4" sx={{ mb: 2 }}>
          Project Management
        </Typography>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}>
          <Box sx={{ paddingY: 2 }}>
            <SideBarTabs tabs={['Projects', 'Client Contact']} selectedTab={sideTabIndex} selectTab={setSideTabIndex} />
          </Box>

          {sideTabIndex === PROJECT_TAB && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
              }}>
              {/* Project Management - Client */}
              <Box sx={{ my: 1 }}>
                <Typography variant="body2">Client Contact</Typography>
                <Select
                  onChange={(selectedItem) => {
                    setClient(selectedItem.target.value);
                  }}
                  sx={{
                    height: '32px',
                    width: '100%',
                  }}
                  value={client}>
                  {Object.entries(clientsWithAll).map(([key, value]) => (
                    <MenuItem key={key} value={value.id}>
                      {value.full_name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              {/* Project Management - Managers */}
              <Box sx={{ my: 1 }}>
                <Typography variant="body2">Manager</Typography>
                <Select
                  onChange={(selectedItem) => {
                    setManager(selectedItem.target.value);
                  }}
                  sx={{
                    height: '32px',
                    width: '100%',
                  }}
                  value={manager}>
                  {Object.entries(managerWithAll).map(([key, value]) => (
                    <MenuItem key={key} value={value.user_id}>
                      {value.full_name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              {/* Project Management - Status */}
              <Box sx={{ my: 1 }}>
                <Typography variant="body2">STATUS</Typography>
                <Select
                  onChange={(selectedItem) => {
                    setJobStatus(selectedItem.target.value);
                  }}
                  sx={{
                    height: '32px',
                    width: '100%',
                  }}
                  value={jobStatus}>
                  {Object.entries(PROJECT_STATUS).map(([key, value]) => (
                    <MenuItem key={key} value={value.value}>
                      {value.name}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              <Box sx={{ my: 1, display: 'flex', justifyContent: 'flex-end' }}>
                <CustomButton
                  text="Add Project"
                  variant="contained"
                  width="medium"
                  testid={'add-project-btn'}
                  onClick={() => dispatch(projectModalActions.openAddModal({ type: PROJECT_MODAL }))}
                />
              </Box>
            </Box>
          )}

          {sideTabIndex === CLIENT_TAB && (
            <Box sx={{ my: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <CustomButton
                text="Add Client Contact"
                variant="contained"
                width="large"
                testid={'add-client-btn'}
                onClick={() => dispatch(projectModalActions.openAddModal({ type: CLIENT_MODAL }))}
              />
            </Box>
          )}
        </Box>
      </FilterPanelContainer>
    );
  };

  return (
    <>
      <PageContainer>
        {sidePanel()}
        {sideTabIndex === PROJECT_TAB && (
          <>
            <ProjectListContainer>
              <Grid container display="flex" flexDirection="row">
                <GridItem title={'PROJECT'} seenOrIsHeading={true} breakpoint={4} />
                <GridItem title={'CLIENT'} seenOrIsHeading={true} breakpoint={1} />
                <GridItem title={'FINISH'} seenOrIsHeading={true} breakpoint={1} />
                <GridItem title={'DURATION'} seenOrIsHeading={true} breakpoint={1} />
                <GridItem title={'COST'} seenOrIsHeading={true} breakpoint={1} />
                <GridItem title={'STATUS'} seenOrIsHeading={true} breakpoint={1} />
                <GridItem title={'% COMPLETE'} seenOrIsHeading={true} breakpoint={1.5} />
                <GridItem title={'RAG'} seenOrIsHeading={true} breakpoint={1} />
                <GridItem title={'+'} seenOrIsHeading={true} breakpoint={0.5} />
              </Grid>

              {projectList && projectList.length > 0 ? (
                projectList.map((item: ProjectResponse) => {
                  return <ProjectLineItem key={item.id} projectData={item} />;
                })
              ) : (
                <Typography variant="body2" sx={{ p: 2, textAlign: 'center' }}>
                  No projects found.
                </Typography>
              )}
            </ProjectListContainer>
            <>
              {open && modalProps?.type === PROJECT_MODAL && (
                <ProjectModal
                  open={open}
                  isEditing={mode === 'edit'}
                  formOptions={formOptions}
                  setSnackBarMessage={setMessage}
                  onClose={() => dispatch(projectModalActions.closeModal())}
                  initialValues={modalProps?.type === PROJECT_MODAL ? modalProps.data : null}
                />
              )}
            </>
          </>
        )}

        {sideTabIndex === CLIENT_TAB && (
          <>
            <ProjectListContainer>
              <Grid container display="flex" flexDirection="row">
                <GridItem title={'CLIENT'} seenOrIsHeading={true} breakpoint={4} />
                <GridItem title={'POSITION'} seenOrIsHeading={true} breakpoint={1} />
                <GridItem title={'NAME'} seenOrIsHeading={true} breakpoint={1} />
                <GridItem title={'EMAIL'} seenOrIsHeading={true} breakpoint={1} />
                <GridItem title={'PHONE'} seenOrIsHeading={true} breakpoint={1} />
              </Grid>

              {projectClients && projectClients.length > 0 ? (
                projectClients.map((clientItem: ClientProjectResponse) => {
                  return <ClientLineItem key={clientItem.id} clientData={clientItem} projectClients={projectClients} />;
                })
              ) : (
                <Typography variant="body2" sx={{ p: 2, textAlign: 'center' }}>
                  No clients found.
                </Typography>
              )}
            </ProjectListContainer>

            <>
              {open && modalProps?.type === CLIENT_MODAL && (
                <ClientContactModal
                  open={open}
                  isEditing={mode === 'edit'}
                  setSnackBarMessage={setMessage}
                  onClose={() => dispatch(projectModalActions.closeModal())}
                  initialValues={modalProps?.type === CLIENT_MODAL ? modalProps.data : null}
                />
              )}
            </>
          </>
        )}

        <SnackBar
          message={message}
          onClose={() => setMessage({ type: '', msg: '' })}
          snackbarOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        />
      </PageContainer>
    </>
  );
};

export default ProjectManagement;
